import {
    AmazonApproval,
    AmazonApprovalTemplatePayload,
    EntityFactory,
    EntityPermissionOperationNames,
    EntityType,
    ExternalReference,
    IEntity,
    IEntityPermission,
    IPageDraft,
    Page,
    PageDraft
} from "@amzn/ask-legal-domain";
import {
    Alert,
    Box,
    Button,
    ButtonProps,
    Grid,
    Icon,
    Link,
    Modal,
    SpaceBetween,
    Spinner,
    TextContent
} from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAPI, useAPI2 } from "../../hooks/api-hook";
import { UIModel } from "../../model/ui-model";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import { getApprovalBaseUrl } from "../../utils/common-utils";
import { EntitySelection } from "../common/EntitySelection";
import { UIField } from "../common/UIField";
import { SelectivePublishModal } from "./SelectivePublishModal";
import "../../styles/component/page-draft/page-draft-v2.scss";

/** Stable empty array to avoid creating new references on every render */
const EMPTY_SECTIONS: Page.Section[] = [];

type ApprovalActions = "resume" | "cancel" | "publish" | "discard";

export const PageDraftApprovalFlowNew = (props: {
    state: UIModel.State<PageDraft.Data>;
    isEditing: boolean;
}) => {
    const context = React.useContext(AppContext);
    const [isAgenticUI] = React.useState(() => context.hasLab(Preference.Lab.AgenticUI));
    const navigate = useNavigate();
    const appStage = context.getStage();
    const AABaseUrl = getApprovalBaseUrl(appStage);

    const [enableApprovalWorkflow, setEnableApprovalWorkflow] = React.useState<boolean>();
    const [authState, setAuthState] = React.useState<"loading" | "authorized" | "unauthorized" | "error">("loading");
    const [submitModalVisible, setSubmitModalVisible] = React.useState<boolean>();
    const [discardModalVisible, setDiscardModalVisible] = React.useState(false);
    const [showDiscardError, setShowDiscardError] = React.useState(false);
    const [
        selectedApprovalTemplate,
        setSelectedApprovalTemplate
    ] = React.useState<ExternalReference.Data<AmazonApprovalTemplatePayload>>();
    const [approval, setApproval] = React.useState<ExternalReference.Data<AmazonApproval>>(null);

    // Selective publish state
    const [publishModalVisible, setPublishModalVisible] = React.useState(false);
    // Track whether current modal session has a publish error (resets on re-open)
    const [currentPublishError, setCurrentPublishError] = React.useState<string | undefined>();
    // Flag to distinguish "user clicked publish" from "modal just reopened with stale Error status"
    const [publishAttemptActive, setPublishAttemptActive] = React.useState(false);
    const loadPageForPublishRunner = useAPI(context.getPageAPI().load2);

    const loadPageLibraryRunner = useAPI(
        context.getPageLibraryAPI().load
    );
    const isPageEditorRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);
    const loadApprovalRunner = useAPI2<IEntity.LoadEntityInput, ExternalReference.Data<AmazonApproval>>(
        context.getCommonAPI().load
    );
    const approvalSubmitRunner = useAPI2(
        context.getPageDraftAPI().submit2
    );
    const publishDraftRunner = useAPI(
        context.getPageDraftAPI().publish
    );
    const refreshApprovalStatusRunner = useAPI2(
        context.getPageDraftAPI().refresh
    );
    const discardDraftRunner = useAPI(
        context.getPageDraftAPI().discard
    );
    const approvalWorkFlowRunner = useAPI2(
        async (action: ApprovalActions) => {
            const api = context.getPageDraftAPI();
            switch (action) {
                case "resume":
                    return await api.resume(props.state.value.id);
                case "cancel":
                    return await api.cancelReview(props.state.value.id);
                default:
                    return;
            }
        }
    );

    const refreshApprovalStatus = () => {
        refreshApprovalStatusRunner.invoke(props.state.value.id);
    };

    const onClick = (action: ApprovalActions) => {
        if (action === "publish") {
            setPublishModalVisible(true);
            setCurrentPublishError(undefined); // Reset error from previous session
            // Load the live page for container comparison in the selective publish modal
            loadPageForPublishRunner.submitRun(
                props.state.value.pageId
            );
        }
        if (action === "resume") {
            approvalWorkFlowRunner.invoke("resume");
        }
        if (action === "cancel") {
            approvalWorkFlowRunner.invoke("cancel");
        }
        if (action === "discard") {
            setDiscardModalVisible(true);
        }
    };

    const onSelectivePublish = (selections: IPageDraft.ContainerPublishSelection[]) => {
        setCurrentPublishError(undefined); // Clear previous error on retry
        setPublishAttemptActive(true); // Mark that a publish attempt is in progress
        publishDraftRunner.submitRun(
            IPageDraft.SelectivePublishInput.create({
                draftId: props.state.value.id,
                containerSelections: selections
            })
        );
        // Do NOT close the modal here — let the useEffect handle it on success
        // so the user can see the loading spinner and any error feedback
    };

    const getApprovalFullUrl = (approvalId: string) => {
        return `${AABaseUrl}Approval/Details/${approvalId}`;
    };

    const discardInitiated = React.useRef(false);

    // Update local state after successful discard
    React.useEffect(() => {
        if (!discardInitiated.current) return;
        if (discardDraftRunner.status === "Succeeded") {
            discardInitiated.current = false;
            setDiscardModalVisible(false);
            props.state.setValue(discardDraftRunner.data.output);
        } else if (discardDraftRunner.status === "Error") {
            discardInitiated.current = false;
            setShowDiscardError(true);
        }
    }, [discardDraftRunner.status, navigate, props.state.value.pageId]);

    const onSubmitForApproval = () => {
        const apiInput = Builder<IPageDraft.SubmitForApprovalInput>()
            .draftId(props.state.value.id)
            .template(EntityFactory.toEntityRef(selectedApprovalTemplate))
            .build();
        approvalSubmitRunner.invoke(
            apiInput
        );
    };

    const onDismissApprovalModal = () => {
        setSubmitModalVisible(false);
        setSelectedApprovalTemplate(undefined);
    };

    const loadApproval = (approvalId: string) => {
        loadApprovalRunner.invoke(
            IEntity.LoadEntityInput.create(
                approvalId,
                EntityType.ExternalReference
            )
        );
    };

    React.useEffect(() => {
        loadPageLibraryRunner.submitRun(
            props.state.value.pageLibraryId
        );
        if (!!props.state.value.approval) {
            // Load approval initially if approval is there
            loadApproval(props.state.value.approval.id);
        }
    }, []);

    React.useEffect(() => {
        if (loadPageLibraryRunner.status === "Succeeded") {
            setEnableApprovalWorkflow(loadPageLibraryRunner.data.output.enableApprovalWorkflow);
            setAuthState("authorized");
        } else if (loadPageLibraryRunner.status === "Error") {
            const err = loadPageLibraryRunner.data?.err;
            if (err?.code === 403) {
                // Page-level editors may not have access to load the PageLibrary.
                // Fall back to checking Page CAN_EDIT directly.
                isPageEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                    props.state.value.pageId,
                    EntityType.Page,
                    EntityPermissionOperationNames.CAN_EDIT
                ));
            } else {
                setAuthState("error");
            }
        }
    }, [loadPageLibraryRunner.status]);

    React.useEffect(() => {
        if (isPageEditorRunner.status === "Succeeded" && isPageEditorRunner.output) {
            setEnableApprovalWorkflow(false);
            setAuthState("authorized");
        } else if (isPageEditorRunner.status === "Succeeded" && !isPageEditorRunner.output) {
            setAuthState("unauthorized");
        } else if (isPageEditorRunner.status === "Error") {
            setAuthState("error");
        }
    }, [isPageEditorRunner.status]);

    React.useEffect(() => {
        if (approvalSubmitRunner.status === "Succeeded") {
            props.state.setValue(approvalSubmitRunner.output);
            setSubmitModalVisible(false);
            setSelectedApprovalTemplate(undefined);
            if (!!approvalSubmitRunner.output.approval) {
                // Load approval when creation is successful
                loadApproval(approvalSubmitRunner.output.approval.id);
            }
        } else if (approvalSubmitRunner.status === "Error") {
            setSelectedApprovalTemplate(undefined);
            // TODO: Alert user with appropriate Error message
        }
    }, [approvalSubmitRunner.status]);

    React.useEffect(() => {
        if (loadApprovalRunner.status === "Succeeded") {
            setApproval(loadApprovalRunner.output);
        }
    }, [loadApprovalRunner.status]);

    React.useEffect(() => {
        if (approvalWorkFlowRunner.status === "Succeeded") {
            if (
                (!!approvalWorkFlowRunner.output.approval && !props.state.value.approval) ||
                (!!approvalWorkFlowRunner.output.approval && !!props.state.value.approval && approvalWorkFlowRunner.output.approval.id !== props.state.value.approval.id) ||
                (!!approvalWorkFlowRunner.output.approval && !!props.state.value.approval && approvalWorkFlowRunner.output.approval.version !== props.state.value.approval.version)
            ) {
                loadApprovalRunner.invoke(
                    IEntity.LoadEntityInput.create(
                        approvalWorkFlowRunner.output.approval!.id,
                        EntityType.ExternalReference
                    )
                );
            }
            props.state.setValue(approvalWorkFlowRunner.output);
        }
    }, [approvalWorkFlowRunner.status]);


    React.useEffect(() => {
        if (
            approvalSubmitRunner.status === "Succeeded" &&
            loadApprovalRunner.status === "Succeeded" &&
            approvalSubmitRunner.output.approval?.id === loadApprovalRunner.output.id
        ) {
            const win = window.open(getApprovalFullUrl(loadApprovalRunner.output.payload.approvalId), "_blank");
            win.focus();
        }
    }, [approvalSubmitRunner.status, loadApprovalRunner.status]);

    React.useEffect(() => {
        if (!publishAttemptActive) return; // Only react to status changes from an active publish attempt

        if (publishDraftRunner.status === "Succeeded") {
            setPublishModalVisible(false);
            setPublishAttemptActive(false);
            setCurrentPublishError(undefined);
            const output = publishDraftRunner.data.output;
            // Check for continuation draft creation failure
            if (output.continuationResult?.status === "failed") {
                // TODO: Show flash/alert to user on the live page
                console.warn("Continuation draft creation failed:", output.continuationResult.errorCode);
            }
            navigate(`/page/${output.newPage.id}/live`);
        } else if (publishDraftRunner.status === "Error") {
            setCurrentPublishError("Publish failed. Please try again.");
            setPublishAttemptActive(false);
        }
    }, [publishDraftRunner.status, publishAttemptActive, navigate]);

    React.useEffect(() => {
        if (refreshApprovalStatusRunner.status === "Succeeded") {
            props.state.setValue(refreshApprovalStatusRunner.output.refreshedDraft);
            setApproval(refreshApprovalStatusRunner.output.refreshedApproval);
        }
    }, [refreshApprovalStatusRunner.status]);

    const getDraftButtons = () => {
        const buttons: ButtonProps[] = [];
        let showRefreshButton = false;
        if (props.state.value.status === "PUBLISHED" || props.state.value.status === "DISCARDED") {
            return;
        }

        const buildDiscardButton = (): ButtonProps =>
            Builder<ButtonProps>()
                .children("Discard Draft")
                .onClick(e => onClick("discard"))
                .variant("normal")
                .disabled(props.isEditing)
                .build();

        if (!enableApprovalWorkflow) {
            if (props.state.value.status === "DRAFT") {
                buttons.push(buildDiscardButton());
            }
            buttons.push(
                Builder<ButtonProps>()
                    .children("Publish")
                    .onClick(e => onClick("publish"))
                    .variant("primary")
                    .disabled(props.isEditing)
                    .build()
            );
        } else {
            if (AABaseUrl && !!props.state.value.approval) {
                if (loadApprovalRunner.status === "Running") {
                    buttons.push(
                        Builder<ButtonProps>()
                            .children("View Approval")
                            .target("_blank")
                            .variant("primary")
                            .loading(true)
                            .disabled(props.isEditing)
                            .build()
                    );
                } else if (!!approval) {
                    if (approval.payload.approvalStatus === "INVALIDATED") {
                        buttons.push(
                            Builder<ButtonProps>()
                                .children("View Invalidated Approval")
                                .href(getApprovalFullUrl(approval.payload.approvalId))
                                .target("_blank")
                                .variant("primary")
                                .disabled(props.isEditing)
                                .build()
                        );
                    } else if (approval.payload.approvalStatus === "REJECTED") {
                        buttons.push(
                            Builder<ButtonProps>()
                                .children("View Rejected Approval")
                                .href(getApprovalFullUrl(approval.payload.approvalId))
                                .target("_blank")
                                .variant("primary")
                                .disabled(props.isEditing)
                                .build()
                        );
                    } else if (approval.payload.approvalStatus === "DRAFT") {
                        buttons.push(
                            Builder<ButtonProps>()
                                .children("Start Approval")
                                .href(getApprovalFullUrl(approval.payload.approvalId))
                                .target("_blank")
                                .variant("primary")
                                .disabled(props.isEditing)
                                .build()
                        );
                    } else {
                        buttons.push(
                            Builder<ButtonProps>()
                                .children("View Approval")
                                .href(getApprovalFullUrl(approval.payload.approvalId))
                                .target("_blank")
                                .variant("primary")
                                .disabled(props.isEditing)
                                .build()
                        );
                    }
                }
            }
            showRefreshButton = true;
            if (props.state.value.status === "DRAFT") {
                buttons.push(buildDiscardButton());
                buttons.push(
                    Builder<ButtonProps>()
                        .children("Submit for Approval")
                        .onClick(e => { setSubmitModalVisible(true); })
                        .variant("primary")
                        .disabled(props.isEditing)
                        .build()
                );
            } else if (props.state.value.status === "PENDING_APPROVAL") {
                buttons.push(
                    Builder<ButtonProps>()
                        .children("Cancel Approval")
                        .onClick(e => onClick("cancel"))
                        .variant("normal")
                        .disabled(props.isEditing)
                        .build()
                );
            } else if (props.state.value.status === "APPROVED") {
                buttons.push(
                    Builder<ButtonProps>()
                        .children("Publish")
                        .onClick(e => onClick("publish"))
                        .variant("primary")
                        .disabled(props.isEditing)
                        .build()
                );
            } else if (props.state.value.status === "REJECTED") {
                buttons.push(
                    Builder<ButtonProps>()
                        .children("Resume Editing")
                        .onClick(e => onClick("resume"))
                        .variant("primary")
                        .disabled(props.isEditing)
                        .build()
                );
            }
        }

        return <SpaceBetween direction="horizontal" size="s">
            {showRefreshButton && <Box margin={{top: "xxs"}}>
                {refreshApprovalStatusRunner.status === "Running" ? (
                    <Spinner/>
                ) : (
                    <div onClick={refreshApprovalStatus}>
                        <Icon name="refresh" />
                    </div>
                )}
            </Box>}
            {buttons.map((b, index) =>
                <Button key={index} {...b}
                    loading={
                        publishDraftRunner.status === "Running" ||
                        approvalSubmitRunner.status === "Running" ||
                        refreshApprovalStatusRunner.status === "Running" ||
                        approvalWorkFlowRunner.status === "Running" ||
                        discardDraftRunner.status === "Running"
                    }
                />
            )}
        </SpaceBetween>;
    };

    const getAlertContent = () => {
        const pathfinderDraftStatus = props.state.value.status;
        if (pathfinderDraftStatus === "PUBLISHED") {
            return "Draft is published, click to create new draft based on current live page";
        }
        if (pathfinderDraftStatus === "DISCARDED") {
            return "This draft has been discarded.";
        }
        if (!enableApprovalWorkflow) {
            return "No approval required for this draft, you can directly publish to live";
        }
        else {
            if (pathfinderDraftStatus === "DRAFT") {
                if (!!approval) {
                    const amazonApprovalStatus = approval.payload.approvalStatus;
                    return `There is an existing Approval request in [${amazonApprovalStatus}] status, click view button to check more details or submit another approval request`;
                }
                return "Please submit for approval";
            }
            if (pathfinderDraftStatus === "PENDING_APPROVAL") {
                if (
                    !!approval &&
                    approval.payload.approvalStatus === "DRAFT"
                ) {
                    return "Please finish submitting your approval through the Approvals tool";
                }
                return `Approval is pending, please wait until all approvers approve your request...`;
            }
            if (pathfinderDraftStatus === "APPROVED") {
                if (
                    !!approval &&
                    approval.payload.approvalStatus === "APPROVED_WITH_CONDITION"
                ) {
                    return "Request is approved with conditions, please View Approval for more details or publish directly if the conditions are met";
                }
                return `Request is approved, please publish the draft`;
            }
            if (pathfinderDraftStatus === "REJECTED") {
                return `Approval is rejected, please resume editing and re-submit for approval`;
            }
        }
    };

    const getAlertType = () => {
        if (props.state.value.status === "DISCARDED") {
            return "info";
        } else if (!enableApprovalWorkflow) {
            return "success";
        } else if (props.state.value.status === "REJECTED") {
            return "error";
        } else if (props.state.value.status === "APPROVED" || props.state.value.status === "PUBLISHED") {
            return "success";
        } else return "info";
    };

    return (
        <React.Fragment>
            <div>
                <Alert
                    dismissible={false}
                    type={authState === "error" || authState === "unauthorized" ? "error" : getAlertType()}>
                    {authState === "loading" && <Spinner/>}
                    {authState === "unauthorized" &&
                        <TextContent>
                            You do not have permission to edit this page draft.
                        </TextContent>}
                    {authState === "error" &&
                        <TextContent>An unexpected error occured, please
                            <span onClick={() => {
                                setAuthState("loading");
                                loadPageLibraryRunner.reload();
                            }}>
                                <Link>Retry</Link>
                            </span>
                        </TextContent>}
                    {authState === "authorized" && isAgenticUI &&
                        <div className="pf-draft-approval">
                            <TextContent>
                                <p>{getAlertContent()}</p>
                            </TextContent>
                            <div className="pf-draft-approval-actions">
                                {getDraftButtons()}
                            </div>
                        </div>}
                    {!isAgenticUI && <>
                    {authState === "authorized" && <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                        <TextContent>
                            <p>{getAlertContent()}</p>
                        </TextContent>
                        <Box float={"right"}>
                            {getDraftButtons()}
                        </Box>
                    </Grid>}
                    </>}
                </Alert>
            </div>
            {submitModalVisible && <Modal
                visible
                header="Submit for approval"
                footer={<Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismissApprovalModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={!selectedApprovalTemplate}
                            loading={approvalSubmitRunner.status === "Running"}
                            onClick={onSubmitForApproval}>
                            Submit
                        </Button>
                    </SpaceBetween>
                </Box>}
                onDismiss={onDismissApprovalModal}
            >
                <UIField.LabelField
                    label="Approval Template"
                >
                    <EntitySelection.Single
                        api={context.getExternalReferenceAPI().loadByBaseEntity}
                        input={EntityFactory.fromEntityAttributes(props.state.value.pageLibraryId, EntityType.PageLibrary)}
                        selected={selectedApprovalTemplate}
                        onSelectionChange={(template) => {
                            setSelectedApprovalTemplate(template as ExternalReference.Data<AmazonApprovalTemplatePayload>);
                        }}
                    />
                </UIField.LabelField>
            </Modal>}
            {publishModalVisible && (
                <SelectivePublishModal
                    visible={publishModalVisible}
                    draftData={props.state.value}
                    liveSections={
                        loadPageForPublishRunner.status === "Succeeded" && loadPageForPublishRunner.data?.output
                        ? loadPageForPublishRunner.data.output.page.sections ?? EMPTY_SECTIONS
                        : EMPTY_SECTIONS
                    }
                    liveSectionsLoading={loadPageForPublishRunner.status === "Running"}
                    liveSectionsError={loadPageForPublishRunner.status === "Error"}
                    loading={publishDraftRunner.status === "Running"}
                    publishError={currentPublishError}
                    onDismiss={() => setPublishModalVisible(false)}
                    onPublish={onSelectivePublish}
                />
            )}
            {discardModalVisible && <Modal
                visible={true}
                header="Discard Draft"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setShowDiscardError(false); setDiscardModalVisible(false); }}
                                disabled={discardDraftRunner.status === "Running"}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => {
                                setShowDiscardError(false);
                                discardInitiated.current = true;
                                discardDraftRunner.submitRun(props.state.value.id);
                            }}
                            loading={discardDraftRunner.status === "Running"}>
                                Discard
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                onDismiss={() => { if (discardDraftRunner.status !== "Running") { setShowDiscardError(false); setDiscardModalVisible(false); } }}>
                <SpaceBetween size="s">
                    <Alert type="warning">
                        Are you sure you want to discard this draft? This action cannot be undone.
                    </Alert>
                    {showDiscardError && (
                        <Alert type="error">
                            Failed to discard draft. Please try again.
                        </Alert>
                    )}
                </SpaceBetween>
            </Modal>}
        </React.Fragment>
    );
};
